import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import axiosInstance from '../../../utils/axios';
import { toast } from 'react-hot-toast';
import { formatINR, formatIndianDate } from '../../../utils/currency';

const AlertManagementPanel = ({ warrantyData = [], onRefresh }) => {
  const [filterType, setFilterType] = useState('all'); // 'all' | 'critical' | 'warning' | 'expired'
  const [acknowledgedIds, setAcknowledgedIds] = useState([]);
  const [renewingId, setRenewingId] = useState(null);

  // Dynamic alert thresholds
  const [thresholds, setThresholds] = useState({
    criticalDays: 30,
    warningDays: 60,
    planningDays: 90
  });

  // Dynamically generate alerts directly from live warranty data
  const dynamicAlerts = useMemo(() => {
    const alerts = [];
    const now = new Date();

    warrantyData.forEach(asset => {
      if (!asset.warrantyEndDate || asset.daysRemaining === null) return;

      const days = asset.daysRemaining;
      const isAck = acknowledgedIds.includes(asset.id);

      if (days < 0) {
        // Expired
        alerts.push({
          id: `alert-exp-${asset.id}`,
          assetId: asset.id,
          rawId: asset.rawId,
          type: 'expired',
          severity: 'critical',
          title: `Warranty Expired: ${asset.name}`,
          message: `Hardware warranty expired ${Math.abs(days)} days ago on ${formatIndianDate(asset.warrantyEndDate)}. Asset is at risk of unprotected repair costs.`,
          asset,
          daysOverdue: Math.abs(days),
          daysRemaining: days,
          acknowledged: isAck,
          cost: asset.costRaw || 0
        });
      } else if (days <= thresholds.criticalDays) {
        // Critical ≤ 30 days
        alerts.push({
          id: `alert-crit-${asset.id}`,
          assetId: asset.id,
          rawId: asset.rawId,
          type: 'critical',
          severity: 'critical',
          title: `Urgent Warranty Expiration: ${asset.name}`,
          message: `Warranty expires in ${days} days (${formatIndianDate(asset.warrantyEndDate)}). Immediate renewal quote requested.`,
          asset,
          daysRemaining: days,
          acknowledged: isAck,
          cost: asset.costRaw || 0
        });
      } else if (days <= thresholds.warningDays) {
        // Warning 31-60 days
        alerts.push({
          id: `alert-warn-${asset.id}`,
          assetId: asset.id,
          rawId: asset.rawId,
          type: 'warning',
          severity: 'warning',
          title: `Renewal Approaching: ${asset.name}`,
          message: `Warranty expires in ${days} days (${formatIndianDate(asset.warrantyEndDate)}). Review renewal budget and vendor SLAs.`,
          asset,
          daysRemaining: days,
          acknowledged: isAck,
          cost: asset.costRaw || 0
        });
      } else if (days <= thresholds.planningDays) {
        // Planning 61-90 days
        alerts.push({
          id: `alert-plan-${asset.id}`,
          assetId: asset.id,
          rawId: asset.rawId,
          type: 'planning',
          severity: 'info',
          title: `Advance Renewal Planning: ${asset.name}`,
          message: `Warranty expires in ${days} days (${formatIndianDate(asset.warrantyEndDate)}). Schedule replacement or extension review.`,
          asset,
          daysRemaining: days,
          acknowledged: isAck,
          cost: asset.costRaw || 0
        });
      }
    });

    // Sort: unacknowledged critical/expired first, then by days remaining
    return alerts.sort((a, b) => {
      if (a.acknowledged !== b.acknowledged) return a.acknowledged ? 1 : -1;
      return a.daysRemaining - b.daysRemaining;
    });
  }, [warrantyData, thresholds, acknowledgedIds]);

  // Filtered alerts
  const filteredAlerts = useMemo(() => {
    if (filterType === 'all') return dynamicAlerts;
    if (filterType === 'critical') return dynamicAlerts.filter(a => a.type === 'critical' || a.type === 'expired');
    if (filterType === 'warning') return dynamicAlerts.filter(a => a.type === 'warning');
    if (filterType === 'expired') return dynamicAlerts.filter(a => a.type === 'expired');
    if (filterType === 'acknowledged') return dynamicAlerts.filter(a => a.acknowledged);
    return dynamicAlerts;
  }, [dynamicAlerts, filterType]);

  // Alert stats
  const alertStats = useMemo(() => {
    let critical = 0;
    let warning = 0;
    let expired = 0;
    let totalRiskValue = 0;

    dynamicAlerts.forEach(a => {
      if (!a.acknowledged) {
        if (a.type === 'expired') expired++;
        else if (a.type === 'critical') critical++;
        else if (a.type === 'warning') warning++;
        totalRiskValue += a.cost;
      }
    });

    return {
      total: dynamicAlerts.length,
      unacknowledged: dynamicAlerts.filter(a => !a.acknowledged).length,
      critical,
      warning,
      expired,
      totalRiskValue
    };
  }, [dynamicAlerts]);

  // Quick 1-click renewal from alert
  const handleAlertRenew = async (assetIdentifier, alertTitle) => {
    try {
      setRenewingId(assetIdentifier);
      const res = await axiosInstance.post('/warranty/renew', {
        asset_id: assetIdentifier,
        period: '12months'
      });

      if (res.data?.success) {
        toast.success(res.data.message || `Warranty renewed for ${alertTitle}!`);
        onRefresh?.();
      } else {
        toast.error('Failed to renew warranty');
      }
    } catch (error) {
      console.error('Renewal error:', error);
      toast.error(error.response?.data?.message || 'Failed to renew warranty');
    } finally {
      setRenewingId(null);
    }
  };

  const toggleAcknowledge = (alertId) => {
    setAcknowledgedIds(prev => 
      prev.includes(alertId) ? prev.filter(id => id !== alertId) : [...prev, alertId]
    );
  };

  const getAlertColorClasses = (type, acknowledged) => {
    if (acknowledged) return 'border-border bg-muted/20 opacity-70';
    switch (type) {
      case 'expired':
        return 'border-destructive/40 bg-destructive/5';
      case 'critical':
        return 'border-error/40 bg-error/5';
      case 'warning':
        return 'border-warning/40 bg-warning/5';
      default:
        return 'border-primary/30 bg-primary/5';
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'expired': return 'AlertOctagon';
      case 'critical': return 'AlertTriangle';
      case 'warning': return 'Clock';
      default: return 'Info';
    }
  };

  return (
    <div className="space-y-6">
      {/* Alert KPI Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="p-4 bg-card border border-border rounded-lg shadow-subtle">
          <div className="text-xs text-muted-foreground uppercase font-semibold mb-1">Active Alerts</div>
          <div className="text-2xl font-bold text-foreground">{alertStats.unacknowledged}</div>
          <p className="text-xs text-muted-foreground mt-0.5">{alertStats.total} total logged</p>
        </div>

        <div className="p-4 bg-card border border-destructive/30 rounded-lg shadow-subtle bg-destructive/[0.02]">
          <div className="text-xs text-destructive uppercase font-semibold mb-1">Expired Warranties</div>
          <div className="text-2xl font-bold text-destructive">{alertStats.expired}</div>
          <p className="text-xs text-destructive font-medium mt-0.5">High immediate risk</p>
        </div>

        <div className="p-4 bg-card border border-error/30 rounded-lg shadow-subtle bg-error/[0.02]">
          <div className="text-xs text-error uppercase font-semibold mb-1">Critical (≤30 Days)</div>
          <div className="text-2xl font-bold text-error">{alertStats.critical}</div>
          <p className="text-xs text-error font-medium mt-0.5">Urgent action needed</p>
        </div>

        <div className="p-4 bg-card border border-primary/30 rounded-lg shadow-subtle bg-primary/[0.02]">
          <div className="text-xs text-primary uppercase font-semibold mb-1">Total At-Risk Value</div>
          <div className="text-2xl font-bold text-primary">{formatINR(alertStats.totalRiskValue, true)}</div>
          <p className="text-xs text-muted-foreground mt-0.5">Unprotected asset value</p>
        </div>
      </div>

      {/* Filter and Configuration Strip */}
      <div className="bg-card border border-border rounded-lg p-5 shadow-subtle">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-card-foreground">Live Warranty Notifications</h3>
            <p className="text-xs text-muted-foreground">Automated thresholds matching real-time expiration timelines</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-smooth ${
                filterType === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              All ({dynamicAlerts.length})
            </button>
            <button
              onClick={() => setFilterType('critical')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-smooth ${
                filterType === 'critical' ? 'bg-error text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Critical ({alertStats.critical + alertStats.expired})
            </button>
            <button
              onClick={() => setFilterType('warning')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-smooth ${
                filterType === 'warning' ? 'bg-warning text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Warning ({alertStats.warning})
            </button>
            <button
              onClick={() => setFilterType('acknowledged')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-smooth ${
                filterType === 'acknowledged' ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              Acknowledged ({acknowledgedIds.length})
            </button>
          </div>
        </div>
      </div>

      {/* Alert Feed */}
      <div className="space-y-3">
        {filteredAlerts.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Icon name="CheckCircle2" size={44} className="text-success mx-auto mb-2 opacity-80" />
            <h4 className="text-base font-semibold text-foreground">No Warranty Alerts</h4>
            <p className="text-xs text-muted-foreground mt-1">
              All warranties are current and outside the alert thresholds.
            </p>
          </div>
        ) : (
          filteredAlerts.map(alert => (
            <div
              key={alert.id}
              className={`border rounded-lg p-4 transition-smooth bg-card shadow-subtle ${getAlertColorClasses(
                alert.type,
                alert.acknowledged
              )}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className={`p-2 rounded-lg flex-shrink-0 mt-0.5 ${
                    alert.type === 'expired' ? 'bg-destructive/10 text-destructive' :
                    alert.type === 'critical' ? 'bg-error/10 text-error' :
                    alert.type === 'warning' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                  }`}>
                    <Icon name={getAlertIcon(alert.type)} size={18} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center space-x-2 flex-wrap gap-y-1 mb-1">
                      <span className="font-bold text-sm text-card-foreground">
                        {alert.title}
                      </span>
                      <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-muted border border-border">
                        {alert.assetId}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {alert.asset.department} • {alert.asset.owner}
                      </span>
                    </div>

                    <p className="text-xs text-muted-foreground mb-2">
                      {alert.message}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 text-xs">
                      <div>
                        <span className="text-muted-foreground">Vendor:</span> <span className="font-semibold text-foreground">{alert.asset.vendor}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Asset Value:</span> <span className="font-bold text-success">{formatINR(alert.cost, false)}</span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Warranty Expiry:</span> <span className="font-semibold text-foreground">{formatIndianDate(alert.asset.warrantyEndDate)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 sm:self-center flex-shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => toggleAcknowledge(alert.assetId)}
                  >
                    {alert.acknowledged ? 'Un-Acknowledge' : 'Acknowledge'}
                  </Button>

                  <Button
                    variant="default"
                    size="sm"
                    className="h-8 text-xs"
                    loading={renewingId === (alert.rawId || alert.assetId)}
                    onClick={() => handleAlertRenew(alert.rawId || alert.assetId, alert.asset.name)}
                    iconName="RefreshCw"
                    iconPosition="left"
                  >
                    Renew (+1 Yr)
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AlertManagementPanel;
